import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { 
  FaGithub, 
  FaLinkedin, 
  FaTwitter, 
  FaDownload,
  FaRocket
} from 'react-icons/fa';
import { HiOutlineMail, HiOutlineBriefcase } from 'react-icons/hi';
import { FiChevronRight } from 'react-icons/fi';

// Components
import Stats from '../components/home/Stats';
import FeaturedProjects from '../components/home/FeaturedProjects';

const Home = () => {
  const [githubStats, setGithubStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch GitHub stats
    const fetchGithubStats = async () => {
      try {
        // Simulate API call
        setGithubStats({
          repos: 15,
          stars: 0,
          followers: 0
        });
      } catch (error) {
        console.error('Error fetching GitHub stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGithubStats();
  }, []);

  return (
    <div className="relative overflow-hidden">
      {/* Interactive Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-500/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 -right-4 w-72 h-72 bg-accent-500/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-600/10 rounded-full blur-3xl" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 min-h-[80vh] flex items-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Available for Remote Work
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight">
                <span className="text-gray-200">Iliyasu</span>
                <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">
                  {' '}Abdurrazaq
                </span>
              </h1>

              <div className="text-xl sm:text-2xl lg:text-3xl text-gray-400 mt-4 h-[60px]">
                <TypeAnimation
                  sequence={[
                    'Full Stack Developer',
                    2000,
                    'Blockchain Engineer',
                    2000,
                    'Embedded Systems Developer',
                    2000,
                    'Tech Innovator',
                    2000,
                  ]}
                  wrapper="span"
                  speed={50}
                  repeat={Infinity}
                />
              </div>

              <p className="text-gray-400 text-lg mt-6 max-w-xl leading-relaxed">
                3+ years designing, building and deploying software across web, 
                backend, blockchain, and embedded systems. Currently pursuing 
                BSc Software Engineering at FUD.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/hire">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2"
                  >
                    <FaRocket />
                    Hire Me
                    <FiChevronRight />
                  </motion.button>
                </Link>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 border border-gray-700 rounded-lg font-medium text-gray-300 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
                >
                  <FaDownload />
                  Download CV
                </motion.button>

                <Link to="/projects">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-6 py-3 border border-primary-500/30 rounded-lg font-medium text-primary-400 hover:bg-primary-500/10 transition-all duration-300 flex items-center gap-2"
                  >
                    <HiOutlineBriefcase />
                    View Projects
                  </motion.button>
                </Link>
              </div>

              {/* Social Links */}
              <div className="flex gap-4 mt-8">
                <a href="https://github.com/developer13-art" target="_blank" rel="noopener noreferrer" 
                   className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white">
                  <FaGithub size={20} />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" 
                   className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white">
                  <FaLinkedin size={20} />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" 
                   className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white">
                  <FaTwitter size={20} />
                </a>
                <a href="mailto:your.email@example.com" 
                   className="p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white">
                  <HiOutlineMail size={20} />
                </a>
              </div>
            </motion.div>

            {/* Right Side - Stats/Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                {/* GitHub Stats Card */}
                <div className="col-span-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-400 text-sm">GitHub Stats</span>
                    <FaGithub className="text-gray-400" size={20} />
                  </div>
                  {loading ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-white/10 rounded w-1/2" />
                      <div className="h-4 bg-white/10 rounded w-1/3" />
                    </div>
                  ) : githubStats && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-2xl font-bold text-white">{githubStats.repos}</div>
                        <div className="text-xs text-gray-400">Repositories</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{githubStats.stars}</div>
                        <div className="text-xs text-gray-400">Stars</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-white">{githubStats.followers}</div>
                        <div className="text-xs text-gray-400">Followers</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-white">3+</div>
                  <div className="text-xs text-gray-400">Years Experience</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-white">15+</div>
                  <div className="text-xs text-gray-400">Projects</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-white">10+</div>
                  <div className="text-xs text-gray-400">Happy Clients</div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4">
                  <div className="text-2xl font-bold text-white">100%</div>
                  <div className="text-xs text-gray-400">Commitment</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <Stats />

      {/* Featured Projects */}
      <FeaturedProjects />

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-primary-500/10 via-accent-500/10 to-primary-500/10 rounded-3xl p-12 border border-white/10 backdrop-blur-xl"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Build Something <span className="bg-gradient-to-r from-primary-400 to-accent-400 bg-clip-text text-transparent">Amazing</span>?
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Let's collaborate on your next project. I'm available for freelance work, 
              full-time positions, and consulting.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/hire">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2"
                >
                  Start a Project
                  <FiChevronRight />
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 border border-gray-700 rounded-xl font-medium text-gray-300 hover:bg-white/5 transition-all duration-300"
                >
                  Get in Touch
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;